import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Building2, Mail, Lock, ArrowRight, Loader2, Eye, EyeOff, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const pwMatch = password && passwordConfirm && password === passwordConfirm;
  const pwLong = password.length >= 6;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== passwordConfirm) { toast.error("Şifreler eşleşmiyor"); return; }
    if (!pwLong) { toast.error("Şifre en az 6 karakter olmalı"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, { email, password, company_name: companyName });
      localStorage.setItem("token", res.data.token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      toast.success("Kayıt başarılı! Profilinizi düzenleyebilirsiniz.");
      navigate("/panel");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-8" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#1B4F72] flex items-center justify-center mx-auto mb-4 rounded-sm">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Firma Kaydı</h1>
          <p className="text-sm text-stone-500 mt-1">Firmanızın profilini oluşturun — tamamen ücretsiz</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white border border-stone-200 p-6 space-y-4">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Firma / Şirket Adı</Label>
            <p className="text-[10px] text-[#1B4F72] mb-1">Kişi adı değil, firma adını yazın.</p>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} required placeholder="ör: Quattro Stand" className="pl-10 rounded-none border-stone-300" data-testid="register-company" />
            </div>
            {companyName && (
              <p className="text-[10px] text-stone-400 mt-1">
                Profil URL: <span className="font-mono text-[#003049]">haberexpo.com/firma/{companyName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}</span>
              </p>
            )}
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">E-posta</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required placeholder="firma@ornek.com" className="pl-10 rounded-none border-stone-300" data-testid="register-email" />
            </div>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Şifre</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPw ? "text" : "password"}
                required
                minLength={6}
                placeholder="En az 6 karakter"
                className="pl-10 pr-10 rounded-none border-stone-300"
                data-testid="register-password"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {password && (
              <div className="flex items-center gap-3 mt-1.5">
                <span className={`text-[10px] flex items-center gap-1 ${pwLong ? "text-green-600" : "text-stone-400"}`}>
                  {pwLong && <Check className="w-3 h-3" />} Min 6 karakter
                </span>
              </div>
            )}
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-stone-500">Şifre Tekrar</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                type={showPw ? "text" : "password"}
                required
                placeholder="Şifrenizi tekrar girin"
                className={`pl-10 rounded-none border-stone-300 ${passwordConfirm && !pwMatch ? "border-red-400" : ""} ${pwMatch ? "border-green-400" : ""}`}
                data-testid="register-password-confirm"
              />
              {pwMatch && <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />}
            </div>
            {passwordConfirm && !pwMatch && (
              <p className="text-[10px] text-red-500 mt-1">Şifreler eşleşmiyor</p>
            )}
          </div>
          <Button type="submit" disabled={loading || !pwMatch} className="w-full rounded-none bg-stone-900 hover:bg-[#1B4F72] font-mono text-xs uppercase tracking-widest h-11 disabled:opacity-50" data-testid="register-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Kayıt Ol <ArrowRight className="w-3.5 h-3.5 ml-2" /></>}
          </Button>
        </form>
        <p className="text-center text-sm text-stone-500 mt-4">
          Zaten hesabınız var mı? <Link to="/giris" className="text-[#1B4F72] hover:underline font-medium">Giriş Yapın</Link>
        </p>
      </div>
    </div>
  );
}
